import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Sliders } from 'lucide-react';

import { CATEGORIES, type Category, tenantConfigApi } from '@/features/tenant-config/api';

const CATEGORY_LABELS: Record<Category, string> = {
  terminology: 'Terminology',
  localization: 'Localization',
  asset_types: 'Asset types',
  document_types: 'Document types',
  workflow: 'Workflow rules',
  alerts: 'Alert defaults',
  roles: 'Roles',
  salary: 'Salary defaults',
  engagement: 'Engagement defaults',
  mobile: 'Mobile behavior',
  notifications: 'Notifications',
  retention: 'Retention policy',
};

export function TenantConfigPage() {
  const [category, setCategory] = useState<Category>('terminology');
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['tenant-config', category],
    queryFn: () => tenantConfigApi.get(category),
  });
  const history = useQuery({
    queryKey: ['tenant-config-history', category],
    queryFn: () => tenantConfigApi.history(category),
  });

  const [editorText, setEditorText] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config.data) {
      setEditorText(JSON.stringify(config.data.payload, null, 2));
      setChangeNote('');
      setError(null);
    }
  }, [config.data, category]);

  const save = useMutation({
    mutationFn: () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = editorText.trim() ? JSON.parse(editorText) : {};
      } catch (err) {
        throw new Error(`Invalid JSON: ${(err as Error).message}`);
      }
      return tenantConfigApi.patch(category, parsed, {
        change_note: changeNote || undefined,
        replace: true,
      });
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-config', category] });
      queryClient.invalidateQueries({ queryKey: ['tenant-config-history', category] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const rollback = useMutation({
    mutationFn: (target_version: number) => tenantConfigApi.rollback(category, target_version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', category] });
      queryClient.invalidateQueries({ queryKey: ['tenant-config-history', category] });
    },
  });

  return (
    <div className="grid h-[calc(100vh-3.5rem)] grid-cols-[220px_1fr] gap-4 p-4">
      <aside className="card overflow-auto !p-0">
        <header className="section-label !mb-0 flex items-center gap-1.5 border-b border-border px-3 py-2">
          <Layers className="h-3.5 w-3.5" />
          Categories
        </header>
        <ul>
          {CATEGORIES.map((c) => (
            <li key={c}>
              <button
                onClick={() => setCategory(c)}
                className={`block w-full border-b border-border px-3 py-2 text-left text-sm ${
                  category === c ? 'bg-muted font-medium' : ''
                }`}
              >
                {CATEGORY_LABELS[c]}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="grid grid-rows-[auto_1fr_auto] gap-3">
        <header className="card flex items-center justify-between !py-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <Sliders className="h-4 w-4 text-primary" />
              {CATEGORY_LABELS[category]}
            </h1>
            <p className="text-xs text-muted-foreground">
              Version {config.data?.version ?? '—'} ·{' '}
              {config.data ? new Date(config.data.updated_at).toLocaleString() : 'loading…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              placeholder="Change note (optional)"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              className="input w-64"
            />
            <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
              Save new version
            </button>
          </div>
        </header>
        <section className="card flex flex-col overflow-hidden !p-0">
          <header className="border-b border-border px-3 py-2 text-xs uppercase text-muted-foreground">
            Payload (JSON)
          </header>
          <textarea
            value={editorText}
            onChange={(e) => setEditorText(e.target.value)}
            className="flex-1 resize-none bg-transparent p-3 font-mono text-sm outline-none"
            spellCheck={false}
          />
          {error && (
            <footer className="border-t border-border bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </footer>
          )}
        </section>
        <HistoryPane
          rows={history.data ?? []}
          loading={history.isLoading}
          onRollback={(v) => rollback.mutate(v)}
          rolling={rollback.isPending}
        />
      </div>
    </div>
  );
}

function HistoryPane({
  rows,
  loading,
  onRollback,
  rolling,
}: {
  rows: Array<{
    id: string;
    version: number;
    change_note: string | null;
    created_at: string;
  }>;
  loading: boolean;
  onRollback: (v: number) => void;
  rolling: boolean;
}) {
  const recent = useMemo(() => rows.slice(0, 6), [rows]);
  return (
    <section className="card !p-0">
      <header className="border-b border-border px-3 py-2 text-xs uppercase text-muted-foreground">
        History (most recent first)
      </header>
      {loading ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">Loading…</p>
      ) : recent.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">No prior versions.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">v</th>
              <th className="px-3 py-2 text-left">Note</th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-2">v{r.version}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.change_note ?? '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    className="btn-secondary text-xs"
                    onClick={() => onRollback(r.version)}
                    disabled={rolling}
                  >
                    Roll back
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
